import * as React from 'react'

import { InFieldLabel, inFieldShellClass, isFieldFilled } from './field'
import { cn } from './utils'

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: React.ReactNode
  description?: string
  noStyle?: boolean
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      description,
      noStyle,
      id,
      value,
      defaultValue,
      onFocus,
      onBlur,
      onChange,
      placeholder,
      'aria-labelledby': _ariaLabelledBy,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId()
    const textareaId = id ?? generatedId
    const [focused, setFocused] = React.useState(false)
    const isControlled = value !== undefined
    const [uncontrolledValue, setUncontrolledValue] = React.useState(
      defaultValue ?? ''
    )
    const currentValue = isControlled ? value : uncontrolledValue
    const floated = Boolean(label) && (focused || isFieldFilled(currentValue))
    const invalid =
      props['aria-invalid'] === true || props['aria-invalid'] === 'true'
    const labeled = Boolean(label)

    return (
      <div className="w-full">
        <div
          className={cn(
            labeled && !noStyle && inFieldShellClass({ multiline: true }),
            labeled && props.disabled && 'opacity-50'
          )}
        >
          {label && (
            <InFieldLabel
              htmlFor={textareaId}
              floated={floated}
              invalid={invalid}
              multiline
            >
              {label}
            </InFieldLabel>
          )}

          <textarea
            id={textareaId}
            className={cn(
              'flex w-full text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 resize-none',
              labeled
                ? cn(
                    'min-h-[7rem] bg-transparent px-4 pb-3 pt-6 outline-none placeholder:text-muted-foreground/60',
                    !floated &&
                      'caret-transparent text-transparent placeholder:text-transparent'
                  )
                : cn(
                    'rounded-md border border-input px-3 py-2 placeholder:text-muted-foreground/60',
                    noStyle ? 'border-none outline-none' : 'input-style'
                  ),
              className
            )}
            rows={4}
            ref={ref}
            value={value}
            defaultValue={defaultValue}
            onFocus={(event) => {
              setFocused(true)
              onFocus?.(event)
            }}
            onBlur={(event) => {
              setFocused(false)
              onBlur?.(event)
            }}
            onChange={(event) => {
              if (!isControlled) setUncontrolledValue(event.target.value)
              onChange?.(event)
            }}
            {...props}
            placeholder={labeled && !floated ? undefined : placeholder}
            aria-labelledby={label ? `${textareaId}-label` : undefined}
          />
        </div>

        {description && (
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        )}
      </div>
    )
  }
)
Textarea.displayName = 'Textarea'

export { Textarea }
